import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { ItemsResponse } from '../../../shared/models/api.model';
import { authKeys } from '../../auth/constants/auth.constant';
import { ledgerKeys } from '../constants/ledger.constant';
import type { ActiveLedger, LedgerSummary } from '../models/ledger.model';
import { ledgerService } from '../services/ledger.service';

export function useLedgerList() {
  return useQuery({ queryKey: ledgerKeys.list(), queryFn: ledgerService.list, select: (r) => r.items });
}

export function pickActive(ledgers: readonly LedgerSummary[]): LedgerSummary | null {
  return ledgers.find((l) => l.isActive) ?? ledgers[0] ?? null;
}

export function useActiveLedger(): ActiveLedger {
  const query = useLedgerList();
  const ledgers = useMemo(() => query.data ?? [], [query.data]);
  return { ledger: pickActive(ledgers), ledgers, isLoading: query.isPending, error: query.error };
}

export function useSwitchLedger(): { switchTo: (ledgerId: string) => void; isSwitching: boolean } {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ledgerService.setActive,
    onMutate: async (ledgerId) => {
      await queryClient.cancelQueries({ queryKey: ledgerKeys.list() });
      queryClient.setQueryData<ItemsResponse<LedgerSummary>>(ledgerKeys.list(), (old) =>
        old ? { items: old.items.map((l) => ({ ...l, isActive: l.id === ledgerId })) } : old,
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ledgerKeys.all });
      void queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
  const { mutate } = mutation;
  const switchTo = useCallback((ledgerId: string) => mutate(ledgerId), [mutate]);
  return { switchTo, isSwitching: mutation.isPending };
}
